import { ApiEndpoints } from '../constants/appConstants';
import httpService from './httpService';

export interface IntakeSurvey {
    surveyId: string;
    surveyName: string;
}

export interface IntakeQuestion {
    questionId: number;
    sequenceNumber: number | null;
    dataType: string | null;
    inputType: string | null;
    questionText: string | null;
    questionGuid: string;
    parentQuestionGuid?: string | null;
    parentQuesResponse?: string | null;
    options?: { value: string | null; sequenceNumber: number | null }[] | null;
    isQuestionRequired: boolean;
    questionAttributes: { attributeName: string | null; attributeValue: string | null; isActive: boolean }[];
}

export interface TargetQuestion {
    questionId: number;
    questionGuid: string | null;
    questionText: string | null;
    measureNumber: string | null;
    tierCode: string | null;
    tierIdentifier: string | null;
    dataFieldType: string | null;
    reviewYear: number | null;
    isActive: boolean;
}

export interface QuestionMapping {
    questionMappingId: number;
    intakeQuestionGuid: string;
    scoringQuestionGuid: string;
    updatedAt: string;
    targetQuestion: TargetQuestion | null;
}

export interface TargetFilters {
    reviewYear: number;
    providerType: string;
    measureNumber: string;
    tier: string;
}

export interface SaveMappingRequest extends TargetFilters {
    surveyId: string;
    intakeQuestionGuid: string;
    scoringQuestionGuid: string;
    expectedUpdatedAt?: string;
    parentQuestionGuid?: string | null;
    conditionalDisplay?: string | null;
}

const baseUrl = `${ApiEndpoints.BASE_URL}/QuestionMapping`;

export function questionAttribute(question: IntakeQuestion, name: string): string {
    return question.questionAttributes.find(attribute => attribute.isActive &&
        attribute.attributeName?.trim().toLowerCase() === name)?.attributeValue?.trim() ?? '';
}

export const questionMappingService = {
    async surveys(reviewYear: number, providerType: string, signal?: AbortSignal) {
        return (await httpService.get<IntakeSurvey[]>(`${baseUrl}/Surveys`, undefined,
            { params: { reviewYear, providerType }, signal })).data;
    },
    async intakeQuestions(surveyId: string, signal?: AbortSignal) {
        return (await httpService.get<IntakeQuestion[]>(`${baseUrl}/IntakeQuestions`, undefined,
            { params: { surveyId }, signal })).data;
    },
    async mappings(signal?: AbortSignal) {
        return (await httpService.get<QuestionMapping[]>(`${baseUrl}/Mappings`, undefined, { signal })).data;
    },
    async targets(filters: TargetFilters, signal?: AbortSignal) {
        return (await httpService.get<TargetQuestion[]>(`${baseUrl}/TargetQuestions`, undefined,
            { params: filters, signal })).data;
    },
    async unmappedTargets(reviewYear: number, providerType: string, signal?: AbortSignal) {
        return (await httpService.get<TargetQuestion[]>(`${baseUrl}/UnmappedTargetQuestions`, undefined,
            { params: { reviewYear, providerType }, signal })).data;
    },
    async save(request: SaveMappingRequest, mappingId?: number) {
        return mappingId
            ? (await httpService.put<QuestionMapping>(`${baseUrl}/Mappings/${mappingId}`, request)).data
            : (await httpService.post<QuestionMapping>(`${baseUrl}/Mappings`, request)).data;
    },
    async deactivate(mappingId: number, expectedUpdatedAt: string) {
        return (await httpService.put<QuestionMapping>(`${baseUrl}/Mappings/${mappingId}/Deactivate`,
            { expectedUpdatedAt })).data;
    }
};